import { Alert, Button, Select, Space, Table, Typography } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import {
    ExternalSourceEntitiesClient,
    ImportHungaryGameResultVm,
    ImportMicroplusGameCommand,
    MicroplusGamesListVm,
    MicroplusPublicGame,
    MicroplusWatchedEventVm,
} from '../../utils/api';

const gameStatus = (game: MicroplusPublicGame): string =>
    game.arenaStatus?.toUpperCase() === 'FINISHED' ? 'FINISHED' : game.status || '';

const importPhase = (game: MicroplusPublicGame): number | undefined =>
    ({ SCHEDULED: 1, LIVE: 2, FINISHED: 3 }[gameStatus(game)]);

export default function MicroplusEventPanel({
    onChange,
}: {
    onChange: (event?: MicroplusWatchedEventVm) => void;
}): React.ReactElement {
    const [events, setEvents] = useState<MicroplusWatchedEventVm[]>([]);
    const [selected, setSelected] = useState<MicroplusWatchedEventVm>();
    const [list, setList] = useState<MicroplusGamesListVm>();
    const [loadingEvents, setLoadingEvents] = useState(true);
    const [error, setError] = useState('');
    const [reload, setReload] = useState(0);
    const [importing, setImporting] = useState<string>();
    const [result, setResult] = useState<ImportHungaryGameResultVm>();
    const mounted = useRef(true);
    useEffect(
        () => () => {
            mounted.current = false;
        },
        []
    );

    const importGame = async (game: MicroplusPublicGame): Promise<void> => {
        const phase = importPhase(game);
        if (!selected?.event || !selected.season || !game.matchKey || !phase || importing) return;
        setImporting(game.matchKey);
        setResult(undefined);
        setError('');
        try {
            const response = await new ExternalSourceEntitiesClient().importMicroplusGame(
                new ImportMicroplusGameCommand({
                    event: selected.event,
                    season: selected.season,
                    matchKey: game.matchKey,
                    phase,
                    replaceEvents: false,
                })
            );
            if (mounted.current) setResult(response);
        } catch {
            if (mounted.current) setError('Could not import Microplus game.');
        } finally {
            if (mounted.current) setImporting(undefined);
        }
    };

    useEffect(() => {
        let active = true;
        setLoadingEvents(true);
        setError('');
        new ExternalSourceEntitiesClient()
            .microplusWatched()
            .then((result) => {
                if (!active) return;
                const configured = (result || []).filter((row) => row.event && row.season);
                setEvents(configured);
                setSelected(configured[0]);
                onChange(configured[0]);
            })
            .catch(() => {
                if (active) setError('Could not load configured Microplus events.');
            })
            .finally(() => {
                if (active) setLoadingEvents(false);
            });
        return () => {
            active = false;
        };
    }, [onChange, reload]);

    useEffect(() => {
        let active = true;
        setList(undefined);
        if (selected?.event) {
            new ExternalSourceEntitiesClient()
                .microplusGames(selected.event)
                .then((result) => {
                    if (active) setList(result);
                })
                .catch(() => {
                    if (active) setError('Could not load Microplus games.');
                });
        }
        return () => {
            active = false;
        };
    }, [selected]);

    return (
        <section aria-label="Microplus events and games" style={{ marginBottom: 24 }}>
            <Space wrap>
                <Select
                    aria-label="Microplus event"
                    placeholder="Event"
                    style={{ width: 260 }}
                    loading={loadingEvents}
                    disabled={loadingEvents || !!importing}
                    value={selected?.event}
                    options={events.map((row) => ({
                        value: row.event,
                        label: `${row.event} · ${row.season}`,
                    }))}
                    onChange={(event) => {
                        const next = events.find((row) => row.event === event);
                        setError('');
                        setResult(undefined);
                        setList(undefined);
                        setSelected(next);
                        onChange(next);
                    }}
                />
                <Button
                    disabled={!!importing}
                    onClick={() => {
                        setResult(undefined);
                        setEvents([]);
                        setSelected(undefined);
                        setList(undefined);
                        onChange(undefined);
                        setReload((value) => value + 1);
                    }}
                >
                    Reload events
                </Button>
            </Space>
            {!loadingEvents && !events.length && !error && (
                <Alert type="info" message="No configured Microplus events are enabled." />
            )}
            {error && <Alert type="error" message={error} />}
            {result && (
                <Alert
                    type={result.refused || result.missing?.length ? 'warning' : 'success'}
                    message={
                        result.note ||
                        (result.refused
                            ? 'Import refused.'
                            : result.alreadyImported
                            ? 'Already imported.'
                            : 'Import complete.')
                    }
                    description={[
                        result.matchId ? `Arena match: ${result.matchId}` : '',
                        ...(result.missing || []),
                    ]
                        .filter(Boolean)
                        .join(' · ')}
                />
            )}
            {selected && (
                <>
                    <Typography.Paragraph>
                        Arena competition: {selected.arenaCompetitionId} · Qualification
                        competition: {selected.qualificationCompetitionId}
                    </Typography.Paragraph>
                    {list?.error && <Alert type="error" message={list.error} />}
                    {list && !list.watched && (
                        <Alert type="warning" message="This event is no longer enabled." />
                    )}
                    <Table
                        rowKey="matchKey"
                        size="small"
                        loading={!list && !error}
                        dataSource={list?.games || []}
                        scroll={{ x: true }}
                        columns={[
                            {
                                title: 'Match',
                                key: 'match',
                                render: (_, row) =>
                                    `${row.homeName || row.homeCode || '—'} – ${
                                        row.awayName || row.awayCode || '—'
                                    }`,
                            },
                            {
                                title: 'Kickoff',
                                key: 'start',
                                render: (_, row) =>
                                    row.startIsPlaceholder
                                        ? 'Time TBC'
                                        : row.start?.toLocaleString() || '—',
                            },
                            { title: 'Status', key: 'status', render: (_, row) => gameStatus(row) },
                            { title: 'Phase', dataIndex: 'phase' },
                            {
                                title: 'Qualification',
                                key: 'qualification',
                                render: (_, row) => (row.isQualification ? 'Yes' : 'No'),
                            },
                            { title: 'Arena competition', dataIndex: 'arenaCompetitionId' },
                            {
                                title: 'Import',
                                key: 'import',
                                render: (_, row) => (
                                    <Button
                                        disabled={
                                            !list?.watched ||
                                            !!list.error ||
                                            !row.matchKey ||
                                            !importPhase(row) ||
                                            !!importing
                                        }
                                        loading={importing === row.matchKey}
                                        onClick={() => void importGame(row)}
                                    >
                                        Import game
                                    </Button>
                                ),
                            },
                        ]}
                    />
                </>
            )}
        </section>
    );
}
